import React, {
    useMemo,
    useState,
} from "react";

import {
    useAutoplaySequence,
} from "../../../../../presentation/public-site/use-autoplay-sequence";
import {withNamespaces} from "react-i18next";
import LandingArrowIcon from "../../ui/LandingArrowIcon";
import {faqItems} from "../../content/faq";
import "./style.less";

const FAQ_AUTOPLAY_INTERVAL_MS = 4500;

const FAQ_ITEM_IDS =
    faqItems.map(
        ({id}) => id
    );

function AnswerIcon() {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M7 4.75H17.25C18.3546 4.75 19.25 5.64543 19.25 6.75V13.75C19.25 14.8546 18.3546 15.75 17.25 15.75H10L5.25 19V6.5C5.25 5.5335 6.0335 4.75 7 4.75Z"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
            />

            <path
                d="M8.5 9H16M8.5 12H13.5"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
            />
        </svg>
    );
}

export function FaqSection({t}) {
    const [activeItemId, setActiveItemId] = useState(
        faqItems[0].id
    );

    const {
        targetRef: faqSectionRef,
        selectItem: selectFaqItem,
        stopAutoplay: stopFaqAutoplay,
    } = useAutoplaySequence({
        itemIds: FAQ_ITEM_IDS,
        activeItemId,
        setActiveItemId,
        intervalMs:
            FAQ_AUTOPLAY_INTERVAL_MS,
        threshold: 0.2,
        rootMargin:
            "0px 0px -8% 0px",
    });

    const activeItem = useMemo(
        () =>
            faqItems.find(
                (item) => item.id === activeItemId
            ) || faqItems[0],
        [activeItemId]
    );

    return (
        <section
            className="landing-faq landing-section landing-section--last"
            id="faq"
            ref={faqSectionRef}
        >
            <div className="landing-faq__decoration"/>

            <div className="container public-site-shell">
                <div className="landing-faq__heading landing-section-heading">
                    <div>
                        <span className="landing-faq__eyebrow landing-section-heading__eyebrow">
                            {t("main.landing.faq.eyebrow")}
                        </span>

                        <h2 className="landing-faq__title landing-section-heading__title">
                            {t("main.landing.faq.title")}
                        </h2>
                    </div>

                    <p className="landing-faq__intro landing-section-heading__intro">
                        {t("main.landing.faq.intro")}
                    </p>
                </div>

                <div className="landing-faq__panel">
                    <div
                        className="landing-faq__questions"
                        role="tablist"
                        aria-label={t("main.landing.faq.tabsAriaLabel")}
                    >
                        {faqItems.map((item, index) => {
                            const active =
                                item.id === activeItemId;

                            return (
                                <button
                                    className={`landing-faq-question${
                                        active
                                            ? " landing-faq-question--active"
                                            : ""
                                    }`}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    aria-controls="landing-faq-answer"
                                    id={`landing-faq-question-${item.id}`}
                                    onClick={() =>
                                        selectFaqItem(item.id)
                                    }
                                    onFocus={stopFaqAutoplay}
                                    key={item.id}
                                >
                                    <span className="landing-faq-question__number">
                                        {String(index + 1).padStart(2, "0")}
                                    </span>

                                    <span className="landing-faq-question__text">
                                        {t(item.questionKey)}
                                    </span>

                                    <span className="landing-faq-question__arrow">
                                        <LandingArrowIcon/>
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    <article
                        className="landing-faq__answer"
                        id="landing-faq-answer"
                        role="tabpanel"
                        aria-labelledby={`landing-faq-question-${activeItem.id}`}
                    >
                        <div className="landing-faq__answer-header">
                            <div className="landing-faq__answer-icon">
                                <AnswerIcon/>
                            </div>

                            <span className="landing-faq__answer-label">
                                {t("main.landing.faq.answerLabel")}
                            </span>
                        </div>

                        <p
                            className="landing-faq__answer-text"
                            key={activeItem.id}
                        >
                            {t(activeItem.answerKey)}
                        </p>

                        <div className="landing-faq__support">
                            <span>{t("main.landing.faq.supportPrompt")}</span>

                            <div>
                                <a href="tel:88002227079">
                                    8 (800) 222-70-79
                                </a>

                                <a href="mailto:support@svetofore.ru">
                                    support@svetofore.ru
                                </a>
                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </section>
    );
}

export default withNamespaces("translation")(FaqSection);
